// Small building blocks for the Help articles: links to another article (optionally at a section),
// links to an app screen, the "Related" line that ends every article, and a callout for the one
// thing a reader must not miss. All links are in-app <Link>s, so they navigate without a reload.
import type { ComponentChildren } from "preact";
import { articleById, articleHref, type ArticleId } from "../articles";
import { Link } from "@nexus/ui";

/** A link to another Help article; the text defaults to the article's title. */
export function HelpLink({ to, section, children }: { to: ArticleId; section?: string | undefined; children?: ComponentChildren }) {
  return <Link href={articleHref(to, section)}>{children ?? articleById(to)?.title ?? to}</Link>;
}

/** A link to a screen of the app, by its path (e.g. "/invoices"). */
export function ScreenLink({ href, children }: { href: string; children: ComponentChildren }) {
  return <Link href={href}>{children}</Link>;
}

/** The "Related:" line at the end of an article: links to other articles, by id. */
export function Related({ to }: { to: readonly ArticleId[] }) {
  return (
    <p class="help-related">
      <span class="help-related-label">Related:</span>
      {to.map((id) => (
        <HelpLink key={id} to={id} />
      ))}
    </p>
  );
}

/** A short boxed note for the one thing a reader must not miss. */
export function Callout({ children }: { children: ComponentChildren }) {
  return <div class="help-callout">{children}</div>;
}

/**
 * The `claude mcp add` command that connects Claude Code to the billing admin's Assistant
 * (FLOW-028, docs/guides/assistant.md#claude-code). The host is the page's own origin. The
 * callback port is the guide's example; any free one works.
 */
export function ClaudeCodeCommand() {
  return (
    <pre>
      <code>{`claude mcp add --transport http --client-id nexus --callback-port 53682 nexus ${window.location.origin}/mcp`}</code>
    </pre>
  );
}
